/**
 * Themes and threads: how a reader groups what they have found, and what they have not.
 *
 * A theme is a hub with prose; a thread is a question still open. Keeping threads distinct
 * from findings is the point — a finding states a claim, a thread admits there is not one
 * yet, and letting the second become the first by accident is how a corpus fills with
 * half-claims wearing a finding's clothes.
 */

import type { JSX } from 'react'

/**
 * The study workspace: the cards a reader works their own corpus in.
 *
 * Separate from `cards.tsx`, which is the *text* — passages, evidence, the
 * lexicon. That file is what the product is for; this one is where the reader
 * keeps what they found. They share the output window and its stack, because a
 * finding is a passage-shaped thing and belongs where passages live, but they
 * are different jobs and a single thousand-line file would hide that.
 *
 * Two rules from the design shape everything here:
 *
 * * **Cards are data, never closures.** Every card in the stack is a plain
 *   value — `{ kind: 'finding', id }`, never `{ onSave: () => … }` — so the
 *   stack stays serialisable and navigation is not coupled to whoever pushed
 *   it. Anything a card needs from React state arrives as a prop on the window.
 * * **Filtering, sorting and paging happen on the server.** A corpus grows
 *   without limit; a page that ships every row and hides most of them gets
 *   slower every time the reader does good work. Sorting one page in the
 *   browser would be worse still — it would reorder the window while claiming
 *   to have sorted the list.
 */

import type {
  ThemeSummary,
  ThreadSummary,
} from '../account'
import {
  createTheme,
  createThread,
  deleteTheme,
  deleteThread,
  listThemes,
  listThreads,
  readTheme,
  readThread,
  updateTheme,
  updateThread,
} from '../account'
import type { Card } from '../cards'
import { t } from '../i18n'

import { Failure, Loading, useLoaded } from './cardDisplay'
import { FormActions, RecordConflict, fromList, toList, useRecordEditor } from './recordEditing'
import { THREAD_STATUSES } from './vocabulary'
import { ListField, SlugOptions } from './findingFields'

// ---------------------------------------------------------------------------
// Themes, threads
// ---------------------------------------------------------------------------

export function ThemesCard({
  corpus,
  onOpen,
}: {
  corpus: string | null
  onOpen: (card: Card) => void
}): JSX.Element {
  const { state, reload } = useLoaded<ThemeSummary[]>(
    () => listThemes(corpus ?? undefined),
    `themes:${corpus}`,
  )

  if (state.status === 'loading') return <Loading />
  if (state.status === 'error') return <Failure message={state.message} />

  return (
    <article>
      <header className="card__head">
        <h2 className="card__kind">{t('themes.title')}</h2>
        <button type="button" className="link" onClick={() => onOpen({ kind: 'themeEditor', id: null })}>
          {t('themes.new')}
        </button>
      </header>
      {state.data.length === 0 && <p className="muted">{t('themes.empty')}</p>}
      <ol className="claims">
        {state.data.map((theme) => (
          <li key={theme.id} className="claim">
            <p className="claim__text">
              <button
                type="button"
                className="link"
                onClick={() => onOpen({ kind: 'themeEditor', id: theme.id })}
              >
                {theme.title}
              </button>
            </p>
            <p className="claim__meta muted">
              {theme.slug} · {t('themes.count', { count: theme.findings })}
              <button
                type="button"
                className="link"
                onClick={async () => {
                  if (!window.confirm(t('themes.confirmDelete'))) return
                  await deleteTheme(theme.id)
                  reload()
                }}
              >
                {t('findings.delete')}
              </button>
            </p>
          </li>
        ))}
      </ol>
      <p className="muted">{t('themes.note')}</p>
    </article>
  )
}

/** A theme as its form holds it: the hub's title, what kind of hub it is, and its prose. */
interface ThemeForm {
  title: string
  category: string
  body: string
}

const BLANK_THEME: ThemeForm = { title: '', category: '', body: '' }

/** The theme form's labels, for the comparison a refused save shows. */
const THEME_LABELS = (): Partial<Record<keyof ThemeForm, string>> => ({
  title: t('editor.title'),
  category: t('themes.category'),
  body: t('themes.hub'),
})

export function ThemeEditorCard({
  id,
  corpus,
  consented,
  onBack,
}: {
  id: number | null
  corpus: string | null
  consented: boolean
  onBack: () => void
}): JSX.Element {
  const editor = useRecordEditor<ThemeSummary, ThemeForm>({
    id,
    blank: BLANK_THEME,
    read: readTheme,
    toForm: (theme) => ({ title: theme.title, category: theme.category ?? '', body: theme.body }),
    create: (form) => createTheme({ ...form, corpus: corpus ?? undefined, consent: !consented }),
    // An emptied category is `null`, not `''`: the column records whether the reader put this
    // hub in a category at all, and a blank string would answer "yes, the empty one".
    update: (themeId, form, rowVersion) =>
      updateTheme(themeId, { ...form, category: form.category || null, row_version: rowVersion }),
    onBack,
  })
  const { fields, set, failure, ready, save } = editor

  if (!ready) return failure ? <Failure message={failure} /> : <Loading />

  return (
    <article>
      <h2 className="card__kind">{id === null ? t('themes.new') : t('themes.edit')}</h2>
      <form className="form" onSubmit={save}>
        <label>
          {t('editor.title')}
          <input
            value={fields.title}
            onChange={(event) => set({ title: event.target.value })}
            required
          />
        </label>
        <label>
          {t('themes.category')}
          <input
            value={fields.category}
            onChange={(event) => set({ category: event.target.value })}
          />
        </label>
        <label>
          {t('themes.hub')}
          <textarea
            value={fields.body}
            onChange={(event) => set({ body: event.target.value })}
            rows={10}
          />
          <span className="muted">{t('themes.hubHint')}</span>
        </label>
        {failure && <Failure message={failure} />}
        <RecordConflict editor={editor} labels={THEME_LABELS()} />
        <FormActions onCancel={onBack} />
      </form>
    </article>
  )
}

export function ThreadsCard({
  corpus,
  onOpen,
}: {
  corpus: string | null
  onOpen: (card: Card) => void
}): JSX.Element {
  const { state, reload } = useLoaded<ThreadSummary[]>(
    () => listThreads(corpus ?? undefined),
    `threads:${corpus}`,
  )

  if (state.status === 'loading') return <Loading />
  if (state.status === 'error') return <Failure message={state.message} />

  return (
    <article>
      <header className="card__head">
        <h2 className="card__kind">{t('threads.title')}</h2>
        <button type="button" className="link" onClick={() => onOpen({ kind: 'threadEditor', id: null })}>
          {t('threads.new')}
        </button>
      </header>
      <p className="muted">{t('threads.note')}</p>
      {state.data.length === 0 && <p className="muted">{t('threads.empty')}</p>}
      <ol className="claims">
        {state.data.map((thread) => (
          <li key={thread.id} className="claim">
            <p className="claim__text">
              <button
                type="button"
                className="link"
                onClick={() => onOpen({ kind: 'threadEditor', id: thread.id })}
              >
                {thread.title}
              </button>
            </p>
            <p className="claim__meta muted">
              {t(`threads.status.${thread.status}`)}
              <button
                type="button"
                className="link"
                onClick={async () => {
                  if (!window.confirm(t('threads.confirmDelete'))) return
                  await deleteThread(thread.id)
                  reload()
                }}
              >
                {t('findings.delete')}
              </button>
            </p>
          </li>
        ))}
      </ol>
    </article>
  )
}

/**
 * A thread as its form holds it.
 *
 * The two lists are comma-separated text here and lists at the boundary, for the same
 * reason as everywhere else in the workspace: a text input holds a string.
 */
interface ThreadForm {
  title: string
  status: string
  body: string
  related: string
  themes: string
}

const BLANK_THREAD: ThreadForm = {
  title: '',
  status: 'open',
  body: '',
  related: '',
  themes: '',
}

/** The form's strings as the fields the server takes. */
function toThreadFields(form: ThreadForm) {
  return {
    title: form.title,
    status: form.status as never,
    body: form.body,
    related: toList(form.related),
    themes: toList(form.themes),
  }
}

/** The open question itself, where it stands, and what it reaches into. */
function ThreadFields({
  fields,
  set,
}: {
  fields: ThreadForm
  set: (patch: Partial<ThreadForm>) => void
}): JSX.Element {
  return (
    <>
      <label>
        {t('threads.question')}
        <textarea
          value={fields.title}
          onChange={(event) => set({ title: event.target.value })}
          rows={2}
          required
        />
      </label>
      <label>
        {t('editor.status')}
        <select value={fields.status} onChange={(event) => set({ status: event.target.value })}>
          {THREAD_STATUSES.map((value) => (
            <option key={value} value={value}>
              {t(`threads.status.${value}`)}
            </option>
          ))}
        </select>
      </label>
      <ListField
        label={t('editor.themes')}
        value={fields.themes}
        onChange={(themes) => set({ themes })}
        options="workspace-themes"
      />
      <ListField
        label={t('threads.related')}
        value={fields.related}
        onChange={(related) => set({ related })}
        options="workspace-findings"
      />
      <label>
        {t('editor.body')}
        <textarea
          value={fields.body}
          onChange={(event) => set({ body: event.target.value })}
          rows={10}
        />
      </label>
    </>
  )
}

export function ThreadEditorCard({
  id,
  corpus,
  consented,
  onBack,
}: {
  id: number | null
  corpus: string | null
  consented: boolean
  onBack: () => void
}): JSX.Element {
  const editor = useRecordEditor<ThreadSummary, ThreadForm>({
    id,
    blank: BLANK_THREAD,
    read: readThread,
    toForm: (thread) => ({
      title: thread.title,
      status: thread.status,
      body: thread.body,
      related: fromList(thread.related),
      themes: fromList(thread.themes),
    }),
    create: (form) =>
      createThread({ ...toThreadFields(form), corpus: corpus ?? undefined, consent: !consented }),
    update: (threadId, form, rowVersion) =>
      updateThread(threadId, { ...toThreadFields(form), row_version: rowVersion }),
    onBack,
  })
  const { fields, set, failure, ready, save } = editor

  if (!ready) return failure ? <Failure message={failure} /> : <Loading />

  return (
    <article>
      <h2 className="card__kind">{id === null ? t('threads.new') : t('threads.edit')}</h2>
      <form className="form" onSubmit={save}>
        <ThreadFields fields={fields} set={set} />
        {failure && <Failure message={failure} />}
        <RecordConflict
          editor={editor}
          labels={{
            title: t('threads.question'),
            status: t('editor.status'),
            body: t('editor.body'),
            related: t('threads.related'),
            themes: t('editor.themes'),
          }}
        />
        <FormActions onCancel={onBack} />
      </form>
      <SlugOptions corpus={corpus} />
    </article>
  )
}

